// Hand-typed TS interfaces that mirror src/chatbot/api/admin/schemas.py.
// Kept in sync manually for now — when the SPA stabilizes we can
// switch to `openapi-typescript` codegen against FastAPI's /openapi.json.

export type DepStatus = "ok" | "degraded" | "down" | "unknown";
export type OverallStatus = "ok" | "degraded" | "down";
export type AnomalySeverity = "info" | "warning" | "critical";

export interface DependencyHealth {
  name: string;
  status: DepStatus;
  latency_ms?: number | null;
  last_success_at?: string | null;
  detail?: string | null;
}

export interface HealthResponse {
  status: OverallStatus;
  timestamp: string;
  version: string;
  dependencies: DependencyHealth[];
}

// One row of the per-mode model table. `effort` is read together with
// `provider`: on "claude" it is output_config.effort, on "gemini" it is
// generationConfig.thinkingConfig; "" means the model's own default on either
// side (same pairing as ModePromptSnapshot). No sampling temperature is set on
// either provider, so none is reported.
export interface ModeConfigSnapshot {
  mode: string;
  model: string;
  provider: string; // "claude" | "gemini"
  effort: string;
  enabled: boolean;
}

export interface AdminDashboardConfig {
  recent_sessions_limit: number;
  max_window_days: number;
}

// Cloud Armor as Terraform attached it to the load balancer (configured
// intent). Live evidence that the edge is in the request path is the
// `edge_geo_header` row of the health strip.
export interface EdgeProtectionSnapshot {
  configured: boolean;
  policy_name: string;
  allowed_countries: string[];
  default_action: string;
  rate_limit_enabled: boolean;
  rate_limit_per_minute: number | null;
  bot_probe_block_enabled: boolean;
  // Optional: older backends omit it.
  widget_assets_public?: boolean;
  app_allowed_countries: string[];
  countries_match_app: boolean | null;
}

export interface ConfigResponse {
  timestamp: string;
  gcp_project_id: string;
  gcp_region: string;
  firestore_database: string;
  prompt_version: string;
  admin_enabled: boolean;
  admin_token_set: boolean;
  multicheck_enabled: boolean;
  model_armor_enabled: boolean;
  reranker_enabled: boolean;
  tracing_enabled: boolean;
  tracing_sample_rate: number;
  metrics_enabled: boolean;
  allowed_countries: string;
  anonymous_question_limit: number;
  rate_limit_per_session: number;
  rate_limit_per_ip: number;
  history_max_turns: number;
  history_soft_max_tokens: number | null;
  session_ttl_days_anonymous: number;
  test_session_keyword_set: boolean;
  modes: ModeConfigSnapshot[];
  admin_dashboard: AdminDashboardConfig;
  // Optional: older backends omit it — render defensively.
  edge_protection?: EdgeProtectionSnapshot | null;
}

// Live system prompts (GET /admin/api/prompts). Mirrors ModePromptSnapshot /
// PromptsResponse in src/chatbot/api/admin/schemas.py. `note` names the
// dynamic parts NOT in the canonical text (locale hint, time context, RAG
// <context> block, sample persona).
//
// `effort` only means something together with `provider`: on Claude it is
// output_config.effort (empty = the adaptive model default), on Gemini it is
// generationConfig.thinkingConfig (empty = the model's own default). Rendering
// one fallback word for both would state a Claude fact about a Gemini mode.
export interface ModePromptSnapshot {
  mode: string;
  label: string;
  // Optional: a backend predating the second provider omits it, so treat a
  // missing value as "claude".
  provider?: string;
  model: string;
  effort: string;
  enabled: boolean;
  prompt_version: string;
  char_count: number;
  note: string;
  system_prompt: string;
}

export interface PromptsResponse {
  timestamp: string;
  prompt_version: string;
  modes: ModePromptSnapshot[];
}

export interface NorthStar {
  turns_total: number;
  sessions_engaged_total: number;
  feedback_positive_ratio: number | null;
  feedback_count: number;
}

export interface AnomalyCard {
  kind: string;
  severity: AnomalySeverity;
  value: number;
  threshold: number;
  detail: string;
}

export interface ModeTransition {
  from_mode: string;
  to_mode: string;
  count: number;
}

// ---------------------------------------------------------------------------
// Partner tenants. A tenant is one commercial party and owns a SET of embed
// origins (apex + wildcard subdomains + staging), so per-partner numbers roll
// up correctly instead of splitting across host strings. Two buckets always
// exist and are never configurable: `unknown` (no valid Origin header) and
// `unmapped` (a real origin no tenant claims) — they keep the tenant rows
// additive against the global total. Mirrors api/origin_tenants.py.
// ---------------------------------------------------------------------------

export const TENANT_UNKNOWN = "unknown";
export const TENANT_UNMAPPED = "unmapped";

export interface TenantOriginCount {
  origin: string;
  sessions: number;
}

export interface TenantInfo {
  tenant: string;
  configured: boolean;
  origin_patterns: string[];
  sessions: number;
  turns: number;
  total_usd: number;
  origins_seen: TenantOriginCount[];
}

export interface TenantsResponse {
  timestamp: string;
  window_label: string;
  include_test: boolean;
  tenants: TenantInfo[];
  unmapped_origins: TenantOriginCount[];
  mapping_configured: boolean;
}

// Every windowed response echoes the applied partner scope. `tenant` is ""
// for unfiltered; `unattributed_turns` counts turns whose parent session
// started before the window and therefore carry no tenant. Both optional —
// an older backend omits them.
export interface TenantScoped {
  tenant?: string;
  unattributed_turns?: number;
}

export interface SnapshotResponse extends TenantScoped {
  timestamp: string;
  window_label: string;
  include_test: boolean;
  turns_count: number;
  sessions_count: number;
  active_sessions_5m: number;
  active_sessions_1h: number;
  active_sessions_24h: number;
  turns_5m: number;
  turns_15m: number;
  turns_60m: number;
  avg_question_count: number;
  north_star: NorthStar;
  anomalies: AnomalyCard[];
  mode_kickoff: Record<string, number>;
  mode_transitions: ModeTransition[];
  locale_distribution: Record<string, number>;
  country_distribution: Record<string, number>;
  device_distribution: Record<string, number>;
  os_distribution: Record<string, number>;
  origin_distribution: Record<string, number>;
  // Same population as origin_distribution, grouped by commercial party.
  tenant_distribution?: Record<string, number>;
}

export interface PeakResponse extends TenantScoped {
  timestamp: string;
  window_label: string;
  include_test: boolean;
  hour_of_day_histogram: number[];
  weekday_histogram: number[];
  hour_weekday_heatmap: number[][];
  locale_hour_heatmap: Record<string, number[]>;
  turns_last_5min: number;
  median_5min_for_same_hour_of_week: number;
  rate_vs_baseline: number | null;
}

export interface DayCost {
  date: string;
  input_usd: number;
  output_usd: number;
  cache_read_usd: number;
  cache_create_usd: number;
  total_usd: number;
  by_mode_usd: Record<string, number>;
}

export interface ModeTokenStats {
  turns: number;
  sessions: number;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_create_tokens: number;
  total_usd: number;
  avg_tokens_per_session: number;
  // Dominant model that priced this mode's turns — by default FAQ and
  // Profession run Haiku 5.5, Trainer, Discovery and Multicheck Sonnet 5.5
  // (~40% of Opus 5 per token). total_usd is already exact per turn; these
  // surface WHICH model/rate priced the row. Gemini is priced at the standard
  // eu rate after the introductory discount — see src/chatbot/core/pricing.py.
  model?: string; // base model id, e.g. "claude-sonnet-5"
  model_label?: string; // board-friendly, e.g. "Sonnet 5"
  input_usd_per_1m?: number;
  output_usd_per_1m?: number;
}

export interface ClaudeCacheSavings {
  cache_read_tokens: number;
  naive_usd_without_cache: number;
  actual_usd_with_cache: number;
  savings_usd: number;
  // Claude writes, their premium over plain input per TTL, and the reads per
  // written token that repay it. Optional: older backends omit them.
  cache_write_tokens?: number;
  write_premium_usd?: number;
  break_even_read_write_ratio?: number | null;
}

export interface CostResponse extends TenantScoped {
  timestamp: string;
  window_label: string;
  include_test: boolean;
  daily: DayCost[];
  per_mode: Record<string, ModeTokenStats>;
  // Same rollup shape, grouped by partner tenant — "what does each partner
  // cost us". Scoped by the tenant filter like every other figure here.
  per_tenant?: Record<string, ModeTokenStats>;
  // `per_tenant` split by mode: {tenant: {mode: stats}}. The modes run on
  // different models at different rates, so this is the grain a partner's
  // invoice line is written at. Optional: older backends omit it.
  per_tenant_mode?: Record<string, Record<string, ModeTokenStats>>;
  total_window_usd: number;
  today_so_far_usd: number;
  today_projected_usd: number;
  last_7d_usd: number;
  monthly_projection_usd: number;
  annual_projection_usd: number;
  claude_cache_savings: ClaudeCacheSavings;
  // false when a turns read came back with read_limit turns, the most one read
  // returns, and so may have lost the oldest turns of its span: `complete` for
  // the window's figures, `last_7d_complete` for the last 7 days and the
  // monthly projection, `last_30d_complete` for the annual projection.
  // Optional: older backends omit them.
  complete?: boolean;
  last_7d_complete?: boolean;
  last_30d_complete?: boolean;
  read_limit?: number;
}

// Cost per person and the scenario calculator (src/chatbot/api/admin/estimate.py).
// A person is a signed-in pseudonym across its sessions, or one anonymous session.
export interface PersonCostStats {
  segment: "all" | "anonymous" | "signed_in";
  people: number;
  sessions: number;
  turns: number;
  mean_usd: number;
  // null below EstimateResponse.min_people.
  p25_usd: number | null;
  median_usd: number | null;
  p90_usd: number | null;
  // People of the group who reached a limit at least once, their share, the
  // people per limit (questions / cost / daily_limit), and whether the share is
  // above limit_warn_share with min_people people. Optional: older backends
  // omit them.
  limit_hit?: number;
  limit_hit_share?: number | null;
  limit_reasons?: Record<string, number>;
  limit_warning?: boolean;
}

export interface ModeUsageProfile {
  mode: string;
  model: string;
  model_label: string;
  turns: number;
  people: number;
  input_tokens_per_turn: number;
  output_tokens_per_turn: number;
  cache_read_tokens_per_turn: number;
  cache_write_tokens_per_turn: number;
  usd_per_turn: number;
}

export interface PricedModel {
  model_id: string;
  label: string;
  provider: "claude" | "gemini";
  input_usd_per_1m: number;
  output_usd_per_1m: number;
  cache_read_usd_per_1m: number;
  cache_create_1h_usd_per_1m: number;
}

export interface ScenarioEstimate {
  scenario: "defensive" | "normal" | "offensive";
  basis: "p25" | "mean" | "p90";
  usd_per_person: number;
  usd_per_month: number;
  // The daily budget bounded usd_per_month. Optional: older backends omit it.
  budget_bound?: boolean;
}

// The limits in force the scenarios honour, in USD; 0 is off.
export interface EstimateCaps {
  anonymous_session_usd: number;
  signed_in_daily_usd: number;
  daily_usd: number;
}

export interface EstimateResponse extends TenantScoped {
  timestamp: string;
  window_label: string;
  include_test: boolean;
  min_people: number;
  per_person: PersonCostStats[];
  profiles: ModeUsageProfile[];
  models: PricedModel[];
  // The model each mode is priced with: the one that answered, or the choice.
  chosen_models: Record<string, string>;
  people_per_month: number;
  // Empty below min_people.
  scenarios: ScenarioEstimate[];
  normal_usd_per_mode: Record<string, number>;
  // Above this share of a group reaching a limit, the limits deserve a look;
  // and the signed-in people who first reached a limit without a login.
  limit_warn_share?: number;
  signed_in_after_free_limit?: number;
  // The limits the costs and scenarios are bounded by; null when priced
  // without them.
  caps?: EstimateCaps | null;
  // false when the turns read came back with read_limit turns, the most one
  // read returns: the oldest days of the 30 are then missing. Optional: older
  // backends omit them.
  complete?: boolean;
  read_limit?: number;
}

// People who chatted (src/chatbot/api/admin/activity.py): a signed-in
// pseudonym, or one anonymous session. Days are Zurich days (YYYY-MM-DD).
export interface DailyActive {
  day: string;
  people: number;
  signed_in: number;
  anonymous: number;
}

// `people` counts every anonymous session, so an anonymous visitor on two
// days counts twice; `avg_per_day` is the figure to compare.
export interface ActivePeriod {
  start: string;
  end: string;
  days: number;
  people: number;
  signed_in: number;
  anonymous: number;
  avg_per_day: number;
}

export interface ActivityComparison {
  current: ActivePeriod;
  previous: ActivePeriod;
  // Relative change of the mean per day; null when the period before had none.
  change: number | null;
}

export interface ActivityResponse extends TenantScoped {
  timestamp: string;
  include_test: boolean;
  // false when the read hit its bound: the oldest days may be short.
  complete: boolean;
  today: DailyActive;
  // The last 30 complete days, oldest first.
  daily: DailyActive[];
  week: ActivityComparison;
  month: ActivityComparison;
}

export interface FeedbackByMode {
  mode: string;
  positive: number;
  negative: number;
  ratio: number | null;
}

export interface FeedbackComment {
  turn_id: string;
  timestamp: string;
  mode: string;
  feedback: string;
  comment: string;
}

// Chat duration per mode: first→last stored turn per (session, mode). The
// averages cover multi-turn chats only — single-turn chats have no span.
export interface ChatDurationByMode {
  mode: string;
  chats: number;
  multi_turn_chats: number;
  avg_duration_seconds: number | null;
  median_duration_seconds: number | null;
  max_duration_seconds: number | null;
}

// The classifier vocabulary the dashboard translates (src/chatbot/core/classify.py):
// the intent of a question or a conversation (INTENT_LABELS, topic first, then
// the question's shape, then «other») and the affect tags of a message. An id
// a newer backend adds reads as itself.
export const INTENT_IDS = [
  "multicheck",
  "basic_check",
  "wayfi",
  "identyfi",
  "berufskompass",
  "assessment",
  "deadline",
  "schnupperlehre",
  "lehrstelle",
  "interview",
  "application",
  "salary",
  "product_cost",
  "duration",
  "requirements",
  "further_education",
  "daily_work",
  "profession_overview",
  "location",
  "employer",
  "privacy",
  "platform",
  "meta_bot",
  "support",
  "comparative",
  "decisional",
  "procedural",
  "definitional",
  "constraint",
  "setback",
  "rejection",
  "affirmation",
  "smalltalk",
  "other",
] as const;

export const AFFECT_IDS = [
  "positive",
  "frustrated",
  "confused",
  "urgent",
  "uncertain",
  "profanity",
] as const;

// The role a place plays in a question (src/chatbot/geo/places.py): the
// person's home, where they search, or a plain mention.
export const PLACE_ROLES = ["home", "target", "mention"] as const;
export type PlaceRole = (typeof PLACE_ROLES)[number];

// Conversations per place, by the role the place played.
export type PlacesByRole = Partial<Record<PlaceRole, Record<string, number>>>;

// An interest term against its comparison: "" is no clear change.
export const INTEREST_TRENDS = ["new", "rising", "falling"] as const;
export type InterestTrend = (typeof INTEREST_TRENDS)[number] | "";

// One row of an interest ranking (the engagement panel and the daily report):
// a term and the conversations asking about it. `baseline` is the comparison's
// count scaled to this span (a seventh of 7 days for one day); null without a
// comparison.
export interface InterestTermRow {
  term: string;
  sessions: number;
  baseline: number | null;
  trend: InterestTrend;
}

// How an Orientierung conversation started: with the Schnellstart's questions
// or in free text (src/chatbot/api/admin/engagement.py).
export const DISCOVERY_STARTS = ["questions", "free_text"] as const;
export type DiscoveryStart = (typeof DISCOVERY_STARTS)[number];

// The conversations of one start, their Orientierung turns in the window and
// the ratings of those turns; `ratio` is positive / rated, null without one.
export interface DiscoveryStartRow {
  start: DiscoveryStart;
  conversations: number;
  turns: number;
  positive: number;
  negative: number;
  ratio: number | null;
}

export interface EngagementResponse extends TenantScoped {
  timestamp: string;
  window_label: string;
  include_test: boolean;
  turns_per_session_histogram: Record<string, number>;
  bounce_rate_by_mode: Record<string, number>;
  feedback_by_mode: Record<string, FeedbackByMode>;
  feedback_comments: FeedbackComment[];
  // Intent per question: a turn that replies to the assistant's own question
  // («Gamen», «B») is counted apart, in `reply_turns`.
  intent_distribution: Record<string, number>;
  mentions_distribution: Record<string, number>;
  berufsfeld_distribution: Record<string, number>;
  traits_distribution: Record<string, Record<string, number>>;
  affect_distribution: Record<string, number>;
  interest_vs_choice_gap: Array<{ interest: string; berufsfeld: string; count: number }>;
  place_distribution: Record<string, number>;
  // Optional: older backends omit it — render defensively.
  duration_by_mode?: Record<string, ChatDurationByMode>;
  // Optional: older backends omit them.
  reply_turns?: number;
  // What each conversation's first real question was about (the title call).
  session_intent_distribution?: Record<string, number>;
  // Conversations per term, most asked first, with the trend against the
  // window of the same length before; `interest_trend_window` names that
  // window, "" when there is no comparison.
  interest_terms?: InterestTermRow[];
  interest_trend_window?: string;
  place_roles?: PlacesByRole;
  // Orientierung conversations by start. Optional: older backends omit it.
  discovery_start?: DiscoveryStartRow[];
  // Beruf conversations per topic of the persona's closed list (every topic,
  // zeros included) and the Beruf conversations in the window. Optional:
  // older backends omit them.
  profession_topics?: Record<string, number>;
  profession_conversations?: number;
}

export interface VerifierSummary {
  verified_turns: number;
  unverified_turns: number;
  avg_confidence: number | null;
  top_issues: Array<[string, number]>;
}

// Retrieval funnel per mode: documents found → pages forwarded → sources
// cited, plus depth. The funnel fields are optional: older backends omit them.
export interface ContextDepthSummary {
  turns: number;
  avg_context_chars: number;
  snippet_fallback_turns: number;
  avg_docs_returned?: number;
  avg_pages_forwarded?: number;
  avg_sources_cited?: number;
  floor_turns?: number;
}

export interface RagAuditResponse extends TenantScoped {
  timestamp: string;
  window_label: string;
  include_test: boolean;
  // Retrieval depth per mode. Optional: older backends omit it.
  context_depth_by_mode?: Record<string, ContextDepthSummary>;
  top_cited_sources: Array<{ url: string; title: string; count: number }>;
  // Orte / Berufe derived from cited junior.gateway.one Lehrstellen (URL +
  // Titel) — WHERE the offers are / WHICH Berufe were shown, distinct by
  // session. Optional: older backends omit them, render defensively.
  offer_places?: Record<string, number>;
  offer_professions?: Record<string, number>;
  source_feedback: Array<{
    url: string;
    positive: number;
    negative: number;
    ratio: number | null;
  }>;
  relevance_histogram_by_mode: Record<string, number[]>;
  no_answer_rate_by_mode: Record<string, number>;
  content_gap_queries: Array<{ query: string; count: number }>;
  verifier: VerifierSummary;
  guardrail_reasons: Record<string, number>;
  streaming_failures: number;
  rag_sanitized_by_source: Record<string, number>;
}

export interface SessionListItem {
  session_id: string;
  created_at: string;
  updated_at: string;
  locale: string;
  country: string;
  device: string;
  chat_mode: string;
  started_modes: string[];
  question_count: number;
  is_test: boolean;
  title: string | null;
  turn_count: number;
  // Review tags computed from the session's turns (additive; older backends
  // omit them, so the SPA renders empty state gracefully).
  feedback_positive?: number;
  feedback_negative?: number;
  flagged_count?: number;
}

export interface SessionListResponse {
  sessions: SessionListItem[];
  timestamp: string;
  limit: number;
}

export interface TurnDetail {
  turn_id: string;
  timestamp: string;
  chat_mode: string;
  model_used: string;
  // How the model ran: "claude" | "gemini", and the effort / thinking level
  // sent ("" = none sent). Empty on turns stored before they were recorded;
  // optional because older backends omit them.
  provider?: string;
  reasoning_depth?: string;
  user_query: string;
  assistant_response: string;
  retrieved_chunks: Array<Record<string, unknown>>;
  input_tokens: number;
  output_tokens: number;
  cache_hit: boolean;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  streaming_completed: boolean;
  guardrail_triggered: boolean;
  guardrail_reason: string | null;
  feedback: string | null;
  feedback_comment: string | null;
  chunks_retrieved: number;
  chunks_used: number;
  max_chunk_relevance_score: number;
  // Retrieval funnel + depth of the turn. Optional: older backends omit them.
  context_chars?: number;
  docs_returned?: number;
  docs_below_floor?: number;
  sources_cited?: number;
  // What retrieval was asked and anchored on, and how long the answer took
  // (request start → first token → done). Optional: older backends omit them.
  search_query?: string;
  retrieval_topic?: string;
  ttft_ms?: number;
  total_latency_ms?: number;
  question_intent: string;
  mentions: string[];
  berufsfeld: string;
  user_affect: string[];
  traits: string[];
}

export interface EventDetail {
  event_id: string;
  timestamp: string;
  session_id: string;
  event_type: string;
  metadata: Record<string, unknown>;
}

export interface SessionDetailResponse {
  session: SessionListItem;
  turns: TurnDetail[];
  events: EventDetail[];
  // The signed-in person's hash, "" for an anonymous session.
  user_pseudonym: string;
}

// Right to erasure (/admindashboard/api, src/chatbot/api/admin_delete.py).
export type UserSessionsBody = { name: string } | { pseudonym: string };

export interface UserSessionSummary {
  session_id: string;
  created_at: string;
  turn_count: number;
  first_message_preview: string;
  chat_mode: string;
  user_role: string;
}

export interface UserSessionsResponse {
  pseudonym: string;
  sessions: UserSessionSummary[];
}

export interface DeleteSessionResponse {
  session_id: string;
  turns_deleted: number;
  events_deleted: number;
  session_existed: boolean;
  audit_event_id: string;
}

export interface EventsTailResponse {
  events: EventDetail[];
  timestamp: string;
}

// Recent Q&A + Sources panel (GET /admin/api/turns/recent). Mirrors
// RecentQAItem / RecentQAResponse in src/chatbot/api/admin/schemas.py.
// Text fields are PII-scrubbed at write time — rendered as INERT plain text
// in the SPA (never via dangerouslySetInnerHTML).
export interface RecentQAItem {
  timestamp: string;
  session_id: string;
  chat_mode: string;
  model_used: string;
  user_query: string;
  assistant_response: string;
  sources: Array<{ source_url: string; title: string; relevance_score: number }>;
  feedback: string | null;
}

export interface RecentQAResponse {
  turns: RecentQAItem[];
  timestamp: string;
  limit: number;
}

export type WindowLabel = "5m" | "1h" | "24h" | "7d" | "30d" | "90d" | "180d" | "365d";

// ---------------------------------------------------------------------------
// Persisted weekly aggregates (analytics_aggregates, written by the task #27
// Cloud Run Job). Surfaced read-only via GET /admin/api/aggregates. Mirrors
// the AggregatesResponse models in src/chatbot/api/admin/schemas.py.
// ---------------------------------------------------------------------------

export interface DemandCell {
  berufsfeld: string;
  locale: string;
  turn_count: number;
}

export interface ContentGapCell {
  berufsfeld: string;
  locale: string;
  no_answer_count: number;
  low_relevance_count: number;
}

export interface PivotCell {
  from_berufsfeld: string;
  to_berufsfeld: string;
  pivot_count: number;
}

export interface WeeklyDemandBlock {
  aggregate_id: string;
  window_label: string;
  time_range_start?: string | null;
  time_range_end?: string | null;
  source_turn_count: number;
  suppressed_cell_count: number;
  k_threshold: number;
  aggregation_job_run_id: string;
  cells: DemandCell[];
}

export interface WeeklyContentGapsBlock {
  aggregate_id: string;
  window_label: string;
  time_range_start?: string | null;
  time_range_end?: string | null;
  source_turn_count: number;
  suppressed_cell_count: number;
  k_threshold: number;
  relevance_floor: number;
  aggregation_job_run_id: string;
  cells: ContentGapCell[];
}

export interface WeeklyPivotMapBlock {
  aggregate_id: string;
  window_label: string;
  time_range_start?: string | null;
  time_range_end?: string | null;
  source_session_count: number;
  suppressed_cell_count: number;
  k_threshold: number;
  aggregation_job_run_id: string;
  cells: PivotCell[];
}

export interface AggregatesResponse {
  available: boolean;
  demand: WeeklyDemandBlock | null;
  content_gaps: WeeklyContentGapsBlock | null;
  pivot_map: WeeklyPivotMapBlock | null;
}

// ---------------------------------------------------------------------------
// Behavioural + safety signals (on-the-fly, GET /admin/api/signals). Mirrors
// SignalsResponse / SafetySignals in src/chatbot/api/admin/schemas.py.
// ---------------------------------------------------------------------------

export interface SafetySignals {
  total_turns: number;
  off_topic_attempts: number;
  system_prompt_attacks: number;
  off_topic_rate: number;
  attack_rate: number;
  off_topic_by_mode: Record<string, number>;
  attack_by_mode: Record<string, number>;
}

export interface MulticheckTimeStat {
  count: number;
  avg_seconds: number;
  median_seconds: number;
}

export interface MulticheckAccuracyStat {
  tasks: number;
  correct: number;
  correct_rate: number;
  // Outcome breakdown. `correct` is «right on the first attempt», and these
  // four partition `tasks` exactly, so the panel must not show `correct`
  // alone: a task solved on the second attempt would read as zero correct
  // with no explanation. Optional because older payloads lack them.
  after_help?: number;
  revealed?: number;
  unsolved?: number;
  skipped?: number;
}

/** How much of the window the accuracy rollups above actually saw. */
export interface MulticheckCoverage {
  sessions_considered: number;
  sessions_omitted: number;
  sessions_truncated: number;
}

export interface SignalsResponse extends TenantScoped {
  timestamp: string;
  window_label: string;
  include_test: boolean;
  safety: SafetySignals;
  self_elimination: Record<string, number>;
  multi_intent_turns: number;
  multi_intent_rate: number;
  geo_radius_turns: number;
  trainer_interview_topics: Record<string, number>;
  multicheck_variants: Record<string, number>;
  multicheck_subtests: Record<string, number>;
  // Zeit-Training answer-time rollups (optional: older payloads lack them).
  multicheck_time_by_subtest?: Record<string, MulticheckTimeStat>;
  multicheck_time_by_variant?: Record<string, MulticheckTimeStat>;
  // Ledger grading rollups (optional: older payloads lack them).
  multicheck_accuracy_by_subtest?: Record<string, MulticheckAccuracyStat>;
  multicheck_accuracy_by_thema?: Record<string, MulticheckAccuracyStat>;
  // Pre-1.17.0 graded turns: one record each, no attempt history, so they
  // cannot be mixed into the rollups above.
  multicheck_legacy_accuracy?: MulticheckAccuracyStat | null;
  multicheck_coverage?: MulticheckCoverage;
  total_sessions: number;
  source_clicks_total: number;
  top_clicked_professions: Record<string, number>;
  continued_after_no_answer_sessions: number;
  mode_switch_distribution: Record<string, number>;
}

// ---------------------------------------------------------------------------
// Talent-Geografie (GET /admin/api/talent-geo). Mirrors TalentGeoResponse in
// src/chatbot/api/admin/schemas.py — demand by home commune vs. shown-offer
// locations, built from the per-turn Lehrstellensuche capture. min_n is the
// k-anonymity threshold: commune rows below it are suppressed server-side.
// ---------------------------------------------------------------------------

export interface TalentGeoCommuneRow {
  bfs: number;
  name: string;
  canton: string;
  searches: number;
  sessions: number;
  zero_result_searches: number;
  median_nearest_minutes: number | null;
  no_offer_within_30: number;
  no_offer_within_60: number;
  top_professions: Record<string, number>;
}

export interface TalentGeoOfferRow {
  bfs: number;
  name: string;
  canton: string;
  impressions: number;
  median_minutes: number | null;
}

export interface TalentGeoResponse extends TenantScoped {
  timestamp: string;
  window_label: string;
  include_test: boolean;
  min_n: number;
  listings_turns: number;
  turns_with_home: number;
  turns_with_commute: number;
  commute_coverage_rate: number;
  zero_result_turns: number;
  demand_by_commune: TalentGeoCommuneRow[];
  demand_by_canton: Record<string, number>;
  offers_shown_by_commune: TalentGeoOfferRow[];
  top_professions: Record<string, number>;
  suppressed_communes: number;
}

// ---------------------------------------------------------------------------
// Success KPI scorecard (GET /admin/api/kpi-scorecard). Mirrors
// KpiScorecardResponse in src/chatbot/api/admin/schemas.py. Actual values, no
// target thresholds — each metric carries `measurable` + `note` for its limits.
// ---------------------------------------------------------------------------

export interface FunnelStage {
  key: string;
  label: string;
  count: number;
  pct_of_opened: number | null;
}

export interface PrivacyWall {
  shown: number;
  accepted: number;
  stopped: number;
  accept_rate: number | null;
  pre_accepted: number;
}

export interface KpiMetric {
  key: string;
  group: string;
  label: string;
  value: number | null;
  numerator: number;
  denominator: number;
  denominator_label: string;
  measurable: boolean;
  note: string;
}

export interface KpiScorecardResponse extends TenantScoped {
  timestamp: string;
  window_label: string;
  include_test: boolean;
  sessions_total: number;
  funnel: FunnelStage[];
  privacy_wall: PrivacyWall;
  metrics: KpiMetric[];
}

// ---------------------------------------------------------------------------
// Partner report (GET /admin/api/partner-report). Mirrors
// PartnerReportResponse in src/chatbot/api/admin/schemas.py — reach, cost,
// quality and a content profile for ONE tenant. `k_threshold` is the
// suppression floor on the content breakdowns (default 10); `suppressed_cells`
// counts what it removed, so a thin report reads as "too little traffic" and
// not as "no interest". `unattributed_turns` and `utility_calls_excluded` are
// the two honesty flags — see the backend docstring.
// ---------------------------------------------------------------------------

export interface PartnerReportResponse {
  timestamp: string;
  window_label: string;
  include_test: boolean;
  tenant: string;
  configured: boolean;
  origin_patterns: string[];
  origins_seen: TenantOriginCount[];
  k_threshold: number;
  unattributed_turns: number;
  utility_calls_excluded: boolean;

  sessions: number;
  turns: number;
  engaged_sessions: number;
  avg_turns_per_session: number;

  total_usd: number;
  usd_per_turn: number;
  usd_per_session: number;
  per_mode: Record<string, ModeTokenStats>;
  daily: DayCost[];

  feedback_positive: number;
  feedback_negative: number;
  feedback_ratio: number | null;
  no_answer_turns: number;
  no_answer_rate: number;
  streaming_failures: number;

  mode_distribution: Record<string, number>;
  locale_distribution: Record<string, number>;
  top_intents: Record<string, number>;
  top_berufsfelder: Record<string, number>;
  suppressed_cells: number;
}

// Runtime switches («Betrieb», src/chatbot/api/admin/runtime.py): what every
// admin may change without a deploy, and the log of who changed what and why.

export type RuntimeProvider = "claude" | "gemini";

// How a mode runs. `depth` is what is sent ("" = none: the model's own
// default, which is `default_depth`).
export interface RuntimeRun {
  provider: string;
  model: string;
  model_label: string;
  depth: string;
  default_depth: string;
}

export interface RuntimeModelChoice {
  model_id: string;
  label: string;
  provider: RuntimeProvider;
  depths: string[];
  default_depth: string;
}

// A mode's switch as stored: null keeps the baseline value, depth "" sends none.
export interface RuntimeSwitch {
  enabled: boolean;
  provider: RuntimeProvider | null;
  model: string | null;
  depth: string | null;
  scope: "all" | "test";
}

export interface RuntimeModeRow {
  mode: string;
  // The Terraform release gate (Multicheck); a mode it holds back cannot run.
  released: boolean;
  enabled: boolean;
  scope: "all" | "test";
  overridden: boolean;
  override: RuntimeSwitch | null;
  baseline: RuntimeRun;
  effective: RuntimeRun;
  test_sessions: RuntimeRun;
  choices: RuntimeModelChoice[];
}

export type BudgetKey = "anonymous_session_usd" | "signed_in_daily_usd" | "daily_usd";

// One ceiling in USD: the Terraform value and the one in effect (0 = off).
export interface RuntimeBudget {
  key: BudgetKey;
  baseline_usd: number;
  effective_usd: number;
  overridden: boolean;
}

export interface RuntimeSpendDay {
  day: string;
  usd: number;
  turns: number;
}

export interface RuntimeSpend {
  today_usd: number;
  today_turns: number;
  daily_budget_usd: number;
  // Today's share of the daily budget; null without one.
  used_ratio: number | null;
  last_30_days_usd: number;
  days: RuntimeSpendDay[];
}

export interface RuntimeLogRow {
  version: number;
  at: string;
  actor: string;
  reason: string;
  change: string;
  summary: string[];
}

// A partner and the pages that may embed the widget; `tenant` keys the partner map.
export interface RuntimeCustomer {
  tenant: string;
  name: string;
  origins: string[];
}

// The daily pause, HH:MM Zurich time; an end before the start runs past midnight.
export interface RuntimeQuietHours {
  start: string;
  end: string;
}

export interface RuntimeView {
  timestamp: string;
  // What a change must name as `expected_version`.
  version: number;
  updated_at: string | null;
  updated_by: string;
  chat_enabled: boolean;
  paused_reason: "switch" | "schedule" | "budget" | null;
  quiet_hours: RuntimeQuietHours | null;
  modes: RuntimeModeRow[];
  budgets: RuntimeBudget[];
  spend: RuntimeSpend;
  log: RuntimeLogRow[];
  // Partners added in the dashboard; what Terraform allows and groups stays read-only.
  customers: RuntimeCustomer[];
  baseline_origins: string[];
  baseline_tenants: RuntimeCustomer[];
  wiped_at: string | null;
}

export interface WipeCounts {
  sessions: number;
  turns: number;
  analytics_events: number;
  analytics_aggregates: number;
}

export interface WipePreview extends WipeCounts {
  // False while no reset password is configured.
  available: boolean;
  // What the admin types to confirm: the project id.
  confirm_text: string;
}

export interface WipeResponse {
  wiped_at: string;
  deleted: WipeCounts;
}

// Every change names the version it was based on and why.
export interface RuntimeChange {
  expected_version: number;
  reason: string;
}

export interface ModeSwitchBody extends RuntimeChange {
  enabled: boolean;
  provider: RuntimeProvider | null;
  model: string | null;
  depth: string | null;
  scope: "all" | "test";
}

export interface BudgetsBody extends RuntimeChange {
  anonymous_session_usd: number | null;
  signed_in_daily_usd: number | null;
  daily_usd: number | null;
}

export interface WipeBody extends RuntimeChange {
  confirm_text: string;
  password: string;
}

// No start and no end: no quiet hours.
export interface QuietHoursBody extends RuntimeChange {
  start: string | null;
  end: string | null;
}

// No origins removes the customer.
export interface CustomerBody extends RuntimeChange {
  name: string;
  origins: string[];
}

// The analysis chat (src/chatbot/api/admin/analyst.py): one earlier message.
export interface AnalystTurn {
  role: "user" | "assistant";
  text: string;
}

export interface AnalystRequest {
  question: string;
  history: AnalystTurn[];
  window: WindowLabel;
  include_test: boolean;
  tenant: string;
  // Scopes the data pack to one Fragen-Check; sent only when set.
  check_id?: string;
}

// An excerpt the answer cites: its number and the session it comes from.
export interface AnalystExcerptRef {
  ref: number;
  session_id: string;
}

export interface AnalystResponse {
  answer: string;
  model: string;
  window_label: string;
  tenant: string;
  include_test: boolean;
  turns_in_window: number;
  excerpts: number;
  excerpt_refs: AnalystExcerptRef[];
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  // The Fragen-Check the answer was about; "" for the window's chat data.
  check_id: string;
}

// The test page's token: the sessions its embed starts are test sessions.
export interface PreviewToken {
  token: string;
  expires_at: string;
}

// ---------------------------------------------------------------------------
// Fragen-Check (/admin/api/question-check/*). Mirrors
// src/chatbot/api/admin/question_check_models.py. Keys, never labels, travel
// for the group kind, the topic, the question type and the verdict; the
// dashboard translates them. A group's `label` is data and shows as it is.
// ---------------------------------------------------------------------------

export const GROUP_KINDS = [
  "beruf",
  "begriff",
  "thema",
  "kein_bildungsthema",
  "unvollstaendig",
  "nicht_zugeordnet",
] as const;
export type GroupKind = (typeof GROUP_KINDS)[number];

// The topics of kind «thema».
export const TOPIC_KEYS = [
  "berufswahl",
  "bewerbung",
  "lehrstellensuche",
  "ueberblick",
  "zukunft",
  "lohn",
  "dauer",
  "nach_der_lehre",
] as const;
export type TopicKey = (typeof TOPIC_KEYS)[number];

// What a row asks, for the «nach Frageart» grouping.
export const QUESTION_TYPES = [
  "was_ist",
  "berufsbild",
  "alltag",
  "lohn",
  "dauer",
  "voraussetzungen",
  "berufswahl",
  "weiterbildung",
  "bewerbung",
  "lehrstelle",
  "ort",
  "vergleich",
  "anmeldung",
  "stichwort",
  "sonstige",
] as const;
export type QuestionType = (typeof QUESTION_TYPES)[number];

// How a checked question went, in the backend's order of precedence: a failed
// run, the canned no-answer, the customer service, a weak best hit (below 0.7),
// an answer citing nothing, an answer.
export const VERDICTS = ["error", "no_page", "support", "weak", "uncited", "answered"] as const;
export type Verdict = (typeof VERDICTS)[number];

// One pasted row: the scrubbed query, its Search Console figures, its group.
export interface QuestionRow {
  // Position in the pasted order, 0-based.
  n: number;
  query: string;
  clicks: number;
  impressions: number;
  // 0 to 1; null when not pasted.
  ctr: number | null;
  position: number | null;
  // The key of the row's QuestionGroup.
  group: string;
  qtype: QuestionType;
}

export interface QuestionGroup {
  // «<kind>:<value>», unique within a list.
  key: string;
  kind: GroupKind;
  // Data (a profession label, a term); "" means: show the translated topic or kind.
  label: string;
  topic: TopicKey | null;
  // QuestionRow.n, best first.
  rows: number[];
  clicks: number;
  impressions: number;
  ctr: number | null;
  // Impression-weighted mean.
  position: number | null;
}

// How the rows were placed: by the rules, by the model, or not at all.
export interface GroupingStats {
  by_rules: number;
  by_model: number;
  unplaced: number;
  duplicates_merged: number;
  model_failed: boolean;
}

export interface QuestionListSummary {
  id: string;
  name: string;
  created_at: string;
  created_by: string;
  row_count: number;
  group_count: number;
}

export interface QuestionList extends QuestionListSummary {
  rows: QuestionRow[];
  groups: QuestionGroup[];
  stats: GroupingStats;
}

export interface QuestionListsResponse {
  lists: QuestionListSummary[];
}

// Tab-separated (an Excel paste, header detected by the backend) or one question per line.
export interface QuestionListCreate {
  name?: string;
  text: string;
}

// A page a check can run from: an embedding origin and its recent sessions.
export interface CheckPage {
  origin: string;
  sessions: number;
}

// What `n` questions cost and take, from recent first FAQ turns.
export interface QuestionCheckEstimate {
  n: number;
  per_question_usd: number;
  total_usd: number;
  seconds_per_question: number;
  total_seconds: number;
  model: string;
  provider: string;
  // The first FAQ turns the means come from.
  basis_turns: number;
  // Fewer than 10 such turns: a fixed assumption instead.
  assumed: boolean;
  search_usd_per_query: number;
  max_questions: number;
  // Most sessions first.
  pages: CheckPage[];
}

export interface QuestionCheckCreate {
  list_id: string;
  // QuestionRow.n, 1 to 100 of them.
  rows: number[];
  page?: string;
}

export interface ResultPage {
  url: string;
  title: string;
  score: number;
}

// The error codes a result carries that the dashboard puts in words: the
// exception a question's run raised, why it left no turn to judge, the
// provider's refusal, and the chat stream's own codes. `http_<status>` is the
// chat's HTTP status; any other code shows as it is.
export const RESULT_ERRORS = [
  "TimeoutError",
  "no_turn",
  "turn_not_found",
  "test_session_unmarked",
  "model_refusal",
  "timeout",
  "llm_error",
  "service_unavailable",
  "chat_paused",
  "mode_disabled",
  "unknown",
] as const;

// One checked question: what the FAQ pipeline did with it.
export interface CheckResult {
  n: number;
  query: string;
  group: string;
  qtype: QuestionType;
  clicks: number;
  impressions: number;
  verdict: Verdict;
  // The Lehrstellen feed answered.
  listings: boolean;
  session_id: string;
  turn_id: string;
  best_url: string;
  best_title: string;
  best_score: number | null;
  pages: ResultPage[];
  // The stored answer, up to 4'000 characters.
  answer: string;
  search_query: string;
  docs_returned: number;
  chunks_used: number;
  sources_cited: number;
  latency_ms: number;
  model: string;
  cost_usd: number;
  error: string;
}

export interface QuestionCheck {
  id: string;
  list_id: string;
  name: string;
  created_at: string;
  created_by: string;
  page: string;
  total: number;
  done: number;
  failed: number;
  status: "running" | "done";
  cost_usd: number;
}

export interface QuestionChecksResponse {
  checks: QuestionCheck[];
}

export interface QuestionCheckDetail extends QuestionCheck {
  results: CheckResult[];
}

// One `/next` step: the check after it, and the questions it finished (none
// while other steps hold the claims).
export interface CheckStepResponse {
  check: QuestionCheck;
  results: CheckResult[];
}

export interface DeletedResponse {
  deleted: boolean;
}

// ---------------------------------------------------------------------------
// Tagesbericht (/admin/api/insights*). Mirrors
// src/chatbot/api/admin/insight_models.py: one report per Zurich day, the
// figures per mode counted by code, and a narrative the analysis-chat model
// writes over them. Days travel as YYYY-MM-DD (Zurich).
// ---------------------------------------------------------------------------

// `created_by` of a report the nightly job made; otherwise an admin's e-mail.
export const INSIGHT_SCHEDULER = "scheduler";

// Why a report has no narrative: a day without conversations calls no model,
// else the model call's failure kind (core/probe.py).
export const INSIGHT_FAILURES = [
  "no_conversations",
  "empty",
  "timeout",
  "quota",
  "auth",
  "not_served",
  "rejected",
  "unexpected",
] as const;

// One mode's day. `questions` leaves out the replies to the assistant's own
// questions (`replies`); `no_page` counts turns the canned no-answer replied
// to, `weak_match` turns whose best page matched below 0,7.
export interface ModeInsight {
  mode: string;
  conversations: number;
  questions: number;
  replies: number;
  feedback_positive: number;
  feedback_negative: number;
  // Conversations of this mode with a single turn that day: asked and left.
  one_question_conversations: number;
  no_page: number;
  weak_match: number;
  intents: Record<string, number>;
  conversation_intents: Record<string, number>;
  terms: InterestTermRow[];
  berufsfelder: Record<string, number>;
  places: PlacesByRole;
  affect: Record<string, number>;
}

// The day in figures; `complete` is false when a read hit its cap.
export interface InsightFigures {
  conversations: number;
  questions: number;
  no_answer_events: number;
  terms: InterestTermRow[];
  modes: ModeInsight[];
  complete: boolean;
}

// A stored report. `partial`: made while its day was still running, so without
// a comparison (`baseline_days` 0; else the days the terms are compared with).
export interface InsightReport {
  day: string;
  partial: boolean;
  created_at: string;
  created_by: string;
  baseline_days: number;
  figures: InsightFigures;
  // Markdown citing excerpts as [#n]; "" with a `failure`.
  narrative: string;
  narrative_model: string;
  excerpt_refs: AnalystExcerptRef[];
  cost_usd: number;
  // "" or one of INSIGHT_FAILURES.
  failure: string;
}

// One row of the report list.
export interface InsightSummary {
  day: string;
  partial: boolean;
  created_at: string;
  created_by: string;
  conversations: number;
  questions: number;
  has_narrative: boolean;
}

// Newest day first.
export interface InsightListResponse {
  reports: InsightSummary[];
}

// Body of POST /insights/run; no day = yesterday, Zurich time.
export interface InsightRunRequest {
  day?: string;
}
